import { useState, useEffect } from 'react';
import { Routine } from '../types/registration';

interface UseAudioPlayerReturn {
  currentlyPlaying: string | null;
  toggleAudio: (audioUrlOrRoutine: string | Routine) => void;
}

/**
 * Custom hook for handling audio playback of routines
 */
const useAudioPlayer = (): UseAudioPlayerReturn => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);

  // Play/pause audio for a routine or direct URL
  const toggleAudio = (audioUrlOrRoutine: string | Routine): void => {
    // Handle both string URLs and routine objects
    const isRoutineObject = typeof audioUrlOrRoutine !== 'string';
    const audioUrl = isRoutineObject ? audioUrlOrRoutine.songFileUrl : audioUrlOrRoutine;
    const audioId = isRoutineObject ? audioUrlOrRoutine.routineId : audioUrl;
    
    // If we're already playing this audio, pause it
    if (currentlyPlaying === audioId) {
      if (audioPlayer) {
        audioPlayer.pause();
      }
      setCurrentlyPlaying(null);
      setAudioPlayer(null);
      return;
    }
    
    // If we're playing something else, stop it
    if (audioPlayer) {
      audioPlayer.pause();
    }
    
    // Only play if there's a valid audio URL
    if (!audioUrl) {
      console.log('No audio URL available');
      return;
    }
    
    // Play the new audio
    const player = new Audio(audioUrl);
    player.addEventListener('ended', () => {
      setCurrentlyPlaying(null);
      setAudioPlayer(null);
    });
    
    player.play().catch(error => {
      console.error('Error playing audio:', error);
      setCurrentlyPlaying(null);
      setAudioPlayer(null);
    });
    
    setCurrentlyPlaying(audioId || audioUrl);
    setAudioPlayer(player);
  };

  // Clean up audio player when component unmounts
  useEffect(() => {
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        setAudioPlayer(null);
      }
    };
  }, [audioPlayer]);

  return {
    currentlyPlaying,
    toggleAudio
  };
};

export default useAudioPlayer; 